import { useState } from 'react'
import { Phone, Mail, CalendarDays, MessageSquare, ClipboardList, Plus, Check, RotateCcw, Trash2, Clock, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { formatDateTime, parseDate } from '@/lib/utils'
import {
  useTasks, useCreateTask, useUpdateTask, useCompleteTask, useReopenTask, useDeleteTask,
  type TaskType, type Task,
} from '@/hooks/useTasks'

const TYPE_OPTIONS: { value: TaskType; label: string; icon: any; color: string }[] = [
  { value: 'task', label: 'Tarefa', icon: ClipboardList, color: 'text-slate-500' },
  { value: 'call', label: 'Ligação', icon: Phone, color: 'text-blue-500' },
  { value: 'email', label: 'Email', icon: Mail, color: 'text-amber-500' },
  { value: 'meeting', label: 'Reunião', icon: CalendarDays, color: 'text-purple-500' },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, color: 'text-green-500' },
]

const TYPE_BY_VALUE = Object.fromEntries(TYPE_OPTIONS.map((t) => [t.value, t]))

interface Props {
  leadId?: string
  opportunityId?: string
}

export function TasksPanel({ leadId, opportunityId }: Props) {
  const { data, isLoading } = useTasks({ lead_id: leadId, opportunity_id: opportunityId })
  const createMut = useCreateTask()
  const updateMut = useUpdateTask()
  const completeMut = useCompleteTask()
  const reopenMut = useReopenTask()
  const deleteMut = useDeleteTask()

  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [type, setType] = useState<TaskType>('task')
  const [dueDate, setDueDate] = useState('')
  const [dueTime, setDueTime] = useState('')
  const [error, setError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<Task | null>(null)

  const tasks = data?.items || []
  const pending = tasks.filter((t) => t.status === 'pending')
  const completed = tasks.filter((t) => t.status === 'done')

  const resetForm = () => {
    setShowForm(false)
    setEditingId(null)
    setTitle('')
    setDueDate('')
    setDueTime('')
    setType('task')
    setError('')
  }

  const startEdit = (t: Task) => {
    setEditingId(t.id)
    setTitle(t.title)
    setType(t.type)
    if (t.due_at) {
      const d = parseDate(t.due_at)
      const pad = (n: number) => String(n).padStart(2, '0')
      setDueDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`)
      setDueTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`)
    } else {
      setDueDate('')
      setDueTime('')
    }
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async () => {
    if (!title.trim()) { setError('Título é obrigatório'); return }
    setError('')
    let due_at: string | null = null
    if (dueDate) {
      const time = dueTime || '09:00'
      const d = new Date(`${dueDate}T${time}`)
      due_at = d.toISOString()
    }
    try {
      if (editingId) {
        await updateMut.mutateAsync({
          id: editingId,
          data: { title: title.trim(), type, due_at },
        })
      } else {
        await createMut.mutateAsync({
          title: title.trim(),
          type,
          due_at,
          lead_id: leadId,
          opportunity_id: opportunityId,
        })
      }
      resetForm()
    } catch (err: any) {
      setError(err?.response?.data?.detail || (editingId ? 'Erro ao salvar' : 'Erro ao criar tarefa'))
    }
  }

  const renderRow = (t: Task) => {
    const tcfg = TYPE_BY_VALUE[t.type]
    const Icon = tcfg?.icon || ClipboardList
    const isDone = t.status === 'done'
    const overdue = !isDone && t.due_at ? parseDate(t.due_at).getTime() < Date.now() : false

    return (
      <div key={t.id} className={`flex items-start gap-3 p-3 rounded-lg border ${isDone ? 'bg-muted/30 opacity-60' : overdue ? 'border-destructive/30 bg-destructive/5' : 'bg-card'}`}>
        <button
          onClick={() => isDone ? reopenMut.mutate(t.id) : completeMut.mutate(t.id)}
          className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isDone ? 'bg-green-500 border-green-500 text-white' : 'border-muted-foreground/40 hover:border-primary'}`}
          title={isDone ? 'Reabrir' : 'Marcar como concluída'}
        >
          {isDone && <Check className="w-3 h-3" />}
        </button>
        <Icon className={`w-4 h-4 mt-1 flex-shrink-0 ${tcfg?.color || ''}`} />
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${isDone ? 'line-through' : ''}`}>{t.title}</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs text-muted-foreground">
            <span>{tcfg?.label || t.type}</span>
            {t.due_at && (
              <span className={`flex items-center gap-1 ${overdue ? 'text-destructive font-medium' : ''}`}>
                <Clock className="w-3 h-3" />{formatDateTime(t.due_at)}{overdue && ' (atrasada)'}
              </span>
            )}
            {t.assigned_to_name && <span>· {t.assigned_to_name}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!isDone && (
            <button onClick={() => startEdit(t)} className="p-1.5 rounded hover:bg-muted text-muted-foreground" title="Editar">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {isDone && (
            <button onClick={() => reopenMut.mutate(t.id)} className="p-1.5 rounded hover:bg-muted text-muted-foreground" title="Reabrir">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={() => setConfirmDelete(t)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive" title="Excluir">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Tarefas e Atividades</h3>
        {!showForm && (
          <Button size="sm" onClick={() => setShowForm(true)} leftIcon={<Plus className="w-4 h-4" />}>
            Nova tarefa
          </Button>
        )}
      </div>

      {showForm && (
        <div className="p-3 rounded-lg border bg-muted/20 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {editingId ? 'Editar tarefa' : 'Nova tarefa'}
            </p>
          </div>
          {error && <div className="text-xs text-destructive">{error}</div>}
          <Input placeholder="O que precisa ser feito?" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={type} onChange={(e) => setType(e.target.value as TaskType)}>
              {TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} placeholder="Vencimento" />
            <Input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} disabled={!dueDate} />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={resetForm}>Cancelar</Button>
            <Button size="sm" onClick={handleSubmit} isLoading={createMut.isPending || updateMut.isPending}>
              {editingId ? 'Salvar alterações' : 'Criar'}
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-6">Carregando…</p>
      ) : tasks.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">Nenhuma tarefa cadastrada. Crie a primeira para começar a organizar follow-ups.</p>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Pendentes ({pending.length})</p>
              {pending.map(renderRow)}
            </div>
          )}
          {completed.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Concluídas ({completed.length})</p>
              {completed.map(renderRow)}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir tarefa?"
        message={`A tarefa "${confirmDelete?.title || ''}" será removida. Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        variant="destructive"
        isLoading={deleteMut.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return
          await deleteMut.mutateAsync(confirmDelete.id)
          setConfirmDelete(null)
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}
